import { Component } from 'react'
import type { ReactNode } from 'react'
import { RefreshCw, AlertTriangle } from 'lucide-react'

declare global {
  interface Window {
    __agentchatRecover?: (opts?: { force?: boolean }) => void
  }
}

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Outermost recovery surface. When any descendant component throws during
 * render, React unmounts the tree → the body becomes a black/white blank
 * (boot-bg). Without this boundary Mark has no recourse but to close and
 * re-open the PWA. With it, he gets a "Restart" button that reuses the
 * same cache-clear + SW-unregister + reload dance the index.html inline
 * script uses for chunk-load failures.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    if (typeof console !== 'undefined') {
      console.error('[AgentChat] render error', error, info?.componentStack)
    }
  }

  private handleRestart = () => {
    if (typeof window.__agentchatRecover === 'function') {
      window.__agentchatRecover({ force: true })
    } else {
      window.location.reload()
    }
  }

  render() {
    if (!this.state.error) return this.props.children

    const message = this.state.error.message || String(this.state.error)

    return (
      <div
        role="alertdialog"
        aria-labelledby="error-boundary-title"
        className="fixed inset-0 z-[2147483647] grid place-items-center
                   bg-stone-50 dark:bg-stone-950
                   text-stone-900 dark:text-stone-100
                   p-6"
        style={{
          paddingTop: 'max(env(safe-area-inset-top, 0px), 24px)',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)',
        }}
      >
        <div className="max-w-sm w-full text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 mb-4
                          rounded-full bg-amber-100 dark:bg-amber-900/40
                          text-amber-700 dark:text-amber-300">
            <AlertTriangle className="w-6 h-6" aria-hidden />
          </div>
          <h1 id="error-boundary-title" className="text-lg font-semibold mb-1">
            AgentChat hit a snag
          </h1>
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-6">
            Tap restart to clear caches and reload. Your chats are safe — they
            live on the server.
          </p>
          <button
            type="button"
            onClick={this.handleRestart}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full
                       bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                       text-white text-sm font-semibold shadow-lg
                       transition-colors"
          >
            <RefreshCw className="w-4 h-4" aria-hidden />
            Restart
          </button>
          {message && (
            <details className="mt-6 text-left">
              <summary className="text-xs text-stone-500 dark:text-stone-500 cursor-pointer
                                  hover:text-stone-700 dark:hover:text-stone-300">
                Show details
              </summary>
              <pre className="mt-2 p-3 rounded-lg
                              bg-stone-100 dark:bg-stone-900
                              text-[11px] text-stone-700 dark:text-stone-400
                              whitespace-pre-wrap break-words overflow-auto
                              max-h-48">
                {message}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
